import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';
import { 
  Users, Calendar, Coins, Award, Gift, FileText, Sun, Share2,
  ArrowRight, Loader2, Plus, Rocket, Sparkles, MapPin, Clock,
  Star, FlaskConical, Code, Cog, Calculator, Bot, Trophy, Heart, Fingerprint, X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const CATEGORY_ICONS = {
  science: FlaskConical, coding: Code, engineering: Cog, math: Calculator, robotics: Bot,
};
const CATEGORY_COLORS = {
  science: 'bg-rose-500', coding: 'bg-blue-500', engineering: 'bg-emerald-500', math: 'bg-purple-500', robotics: 'bg-orange-500',
};

export default function ParentDashboard() {
  const { api, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ learners: [], enrollments: [], totalPoints: 0, badges: 0 });
  const [referralCode, setReferralCode] = useState('');
  const [referralStats, setReferralStats] = useState(null);
  const [campContext, setCampContext] = useState(null);
  const [showPasskeyPrompt, setShowPasskeyPrompt] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Check for pending camp registration context
    const fromCamp = searchParams.get('from') === 'camp';
    const savedContext = localStorage.getItem('pendingCampRegistration');
    if (savedContext) {
      try {
        setCampContext(JSON.parse(savedContext));
      } catch {}
    }

    // Check if user should be prompted to set up passkey
    if (window.PublicKeyCredential && !localStorage.getItem('passkey_prompt_dismissed')) {
      api.get('/auth/passkey/my-passkeys')
        .then(res => {
          if (res.data.length === 0) setShowPasskeyPrompt(true);
        })
        .catch(() => {});
    }
  }, [searchParams, api]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [learnersRes, enrollmentsRes] = await Promise.all([
          api.get('/learners/'),
          api.get('/enrollments/'),
        ]);
        const learners = learnersRes.data?.results || learnersRes.data || [];
        const enrollments = enrollmentsRes.data?.results || enrollmentsRes.data || [];
        const totalPoints = learners.reduce((sum, l) => sum + (l.points_balance || 0), 0);
        setStats({ learners, enrollments, totalPoints, badges: 0 });

        // Fetch referral code
        try {
          const refRes = await api.get('/referral/my-code');
          setReferralCode(refRes.data?.code || '');
          const refStats = await api.get('/referral/stats');
          setReferralStats(refStats.data);
        } catch {}
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, [api]);

  const activeEnrollments = stats.enrollments.filter(e => e.status === 'confirmed');
  const waitlistedEnrollments = stats.enrollments.filter(e => e.status === 'waitlisted');

  const handleSetupPasskey = async () => {
    try {
      const b64encode = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf))).replace(/\+/g,'-').replace(/\//g,'_').replace(/=+$/,'');
      const b64decode = (str) => { str = str.replace(/-/g,'+').replace(/_/g,'/'); while(str.length%4) str+='='; const b=atob(str); return Uint8Array.from(b,c=>c.charCodeAt(0)).buffer; };

      // Get registration options
      const optRes = await api.post('/auth/passkey/register/options', { device_name: navigator.platform || 'My Device' });
      const options = optRes.data;

      // Call WebAuthn API to create credential
      const credential = await navigator.credentials.create({
        publicKey: {
          challenge: b64decode(options.challenge),
          rp: { id: options.rp.id, name: options.rp.name },
          user: { id: b64decode(options.user.id), name: options.user.name, displayName: options.user.displayName },
          pubKeyCredParams: options.pubKeyCredParams,
          timeout: options.timeout,
          authenticatorSelection: options.authenticatorSelection,
          attestation: options.attestation,
          excludeCredentials: (options.excludeCredentials || []).map(c => ({ type: c.type, id: b64decode(c.id) })),
        },
      });

      // Send to server for verification
      await api.post('/auth/passkey/register/verify', {
        id: b64encode(credential.rawId),
        response: {
          attestationObject: b64encode(credential.response.attestationObject),
          clientDataJSON: b64encode(credential.response.clientDataJSON),
        },
      });

      toast.success('Passkey registered! You can now sign in with Face ID or fingerprint.');
      setShowPasskeyPrompt(false);
    } catch (error) {
      if (error.name === 'NotAllowedError') {
        toast.error('Biometric setup was cancelled');
      } else {
        console.error('Passkey setup error:', error);
        toast.error('Could not set up passkey. Your browser may not support this feature.');
      }
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="parent-dashboard">
      {/* Welcome */}
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold mb-1">
          Welcome back, {user?.first_name}! 👋
        </h1>
        <p className="text-muted-foreground">Manage your family's STEM journey from here.</p>
      </div>

      {/* Pending Camp Registration Banner */}
      {campContext && (
        <Card className="border-2 border-rsc-yellow bg-rsc-yellow/5 shadow-lg" data-testid="camp-context-banner">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-rsc-yellow/20 flex items-center justify-center flex-shrink-0">
                <Sun className="h-5 w-5 text-rsc-yellow" />
              </div>
              <h3 className="font-bold text-sm text-rsc-navy">You were registering for Summer Camp!</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              {campContext.campName ? (
                <>You selected <strong>{campContext.campName}</strong>{campContext.locationName ? <> at <strong>{campContext.locationName}</strong></> : ''}. </>
              ) : 'You were browsing summer camps. '}
              {stats.learners.length === 0
                ? 'Add your child below, then we\'ll take you right back to complete your registration.'
                : 'Click below to go back and finish your registration.'}
            </p>
            <div className="flex gap-2">
              {stats.learners.length === 0 ? (
                <Link to="/parent/learners?from=camp">
                  <Button size="sm" className="bg-rsc-yellow text-rsc-navy font-bold rounded-full">
                    <Plus className="w-4 h-4 mr-1.5" /> Add Your Child First
                  </Button>
                </Link>
              ) : (
                <Link to={`/summer-camps?register=${campContext.campId || ''}`}>
                  <Button size="sm" className="bg-rsc-yellow text-rsc-navy font-bold rounded-full">
                    <ArrowRight className="w-4 h-4 mr-1.5" /> Continue Registration
                  </Button>
                </Link>
              )}
              <Button size="sm" variant="ghost" className="text-muted-foreground text-xs" onClick={() => { localStorage.removeItem('pendingCampRegistration'); setCampContext(null); }}>
                Dismiss
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trust This Device — Passkey Setup Prompt */}
      {showPasskeyPrompt && (
        <Card className="border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-sm" data-testid="passkey-prompt">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-rsc-navy flex items-center justify-center flex-shrink-0">
              <Fingerprint className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-sm text-rsc-navy">Trust this device?</h3>
              <p className="text-xs text-gray-500">Enable Face ID, Touch ID, or fingerprint for faster sign-in next time.</p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <Button size="sm" onClick={handleSetupPasskey} className="bg-rsc-navy text-white rounded-full text-xs px-4" data-testid="setup-passkey-btn">
                <Fingerprint className="w-3.5 h-3.5 mr-1.5" /> Enable
              </Button>
              <Button size="sm" variant="ghost" className="text-gray-400 text-xs" onClick={() => { setShowPasskeyPrompt(false); localStorage.setItem('passkey_prompt_dismissed', '1'); }}>
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEM Readiness Score Banner */}
      <Link to="/parent/stem-score">
        <Card className="overflow-hidden border-0 bg-gradient-to-r from-[#1a1147] via-[#2d1b69] to-[#1e3a5f] text-white hover:shadow-xl transition-shadow cursor-pointer group">
          <CardContent className="p-5 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
                <Rocket className="h-6 w-6 text-indigo-300" />
              </div>
              <div>
                <h3 className="font-bold text-base flex items-center gap-2">STEM Readiness Score <Sparkles className="h-4 w-4 text-yellow-400" /></h3>
                <p className="text-white/60 text-sm">Track progress, unlock milestones, and earn exclusive rewards</p>
              </div>
            </div>
            <ArrowRight className="h-5 w-5 text-white/60 group-hover:translate-x-1 transition-transform" />
          </CardContent>
        </Card>
      </Link>

      {/* Stats + Summer Camp CTA Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Learners', value: stats.learners.length, icon: Users, href: '/parent/learners', color: 'text-blue-500' },
          { label: 'Active', value: activeEnrollments.length, icon: Calendar, href: '/parent/enrollments', color: 'text-green-500' },
          { label: 'Points', value: stats.totalPoints, icon: Coins, href: '/parent/points', color: 'text-amber-500' },
          { label: 'Badges', value: stats.badges, icon: Award, href: '/parent/badges', color: 'text-purple-500' },
        ].map((stat, i) => {
          const Icon = stat.icon;
          return (
            <Link key={stat.label} to={stat.href}>
              <Card className="card-dashboard hover:shadow-md transition-shadow h-full">
                <CardContent className="p-4 text-center">
                  <Icon className={`h-5 w-5 mx-auto mb-1 ${stat.color}`} />
                  <p className="text-2xl font-bold">{stat.value}</p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </CardContent>
              </Card>
            </Link>
          );
        })}
        {/* Summer Camp CTA */}
        <Link to="/summer-camps">
          <Card className="bg-gradient-to-br from-rsc-yellow/10 to-orange-50 border-rsc-yellow/30 hover:shadow-md transition-shadow h-full">
            <CardContent className="p-4 text-center">
              <Sun className="h-5 w-5 mx-auto mb-1 text-rsc-yellow" />
              <p className="text-sm font-bold text-rsc-navy">Summer Camps</p>
              <p className="text-[10px] text-muted-foreground">Register Now →</p>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { href: '/catalog', icon: Calendar, label: 'Browse Courses', color: 'bg-primary' },
          { href: '/calendar', icon: MapPin, label: 'Course Calendar', color: 'bg-emerald-500' },
          { href: '/parent/rewards', icon: Gift, label: 'Rewards Store', color: 'bg-secondary' },
          { href: '/parent/forms', icon: FileText, label: 'Forms & Waivers', color: 'bg-accent' },
        ].map((action, i) => {
          const Icon = action.icon;
          return (
            <Link key={action.href} to={action.href}>
              <Card className="card-dashboard hover:shadow-md transition-shadow cursor-pointer h-full">
                <CardContent className="p-3 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${action.color} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <span className="font-medium text-sm">{action.label}</span>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: Learners + Enrollments */}
        <div className="lg:col-span-2 space-y-6">
          {/* Learners */}
          <Card className="card-dashboard">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="flex items-center gap-2 text-base"><Users className="h-5 w-5" /> My Learners</CardTitle>
              <Link to="/parent/learners"><Button variant="outline" size="sm"><Plus className="h-4 w-4 mr-1" /> Add</Button></Link>
            </CardHeader>
            <CardContent>
              {stats.learners.length === 0 ? (
                <div className="text-center py-6">
                  <Users className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground mb-3">No learners added yet</p>
                  <Link to="/parent/learners"><Button size="sm">Add Your First Learner</Button></Link>
                </div>
              ) : (
                <div className="space-y-2">
                  {stats.learners.map((learner, i) => (
                    <div key={learner.id} className="p-3 rounded-lg bg-muted/50 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-primary font-bold text-sm">{learner.first_name?.[0]}{learner.last_name?.[0]}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{learner.first_name} {learner.last_name}</p>
                        <p className="text-xs text-muted-foreground">Grade {learner.grade} · {learner.points_balance || 0} pts</p>
                      </div>
                      <Link to="/parent/stem-score"><Badge variant="outline" className="text-[10px]">STEM Score →</Badge></Link>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming / Active Enrollments */}
          <Card className="card-dashboard">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="flex items-center gap-2 text-base"><Calendar className="h-5 w-5" /> Upcoming Sessions</CardTitle>
              <Link to="/parent/enrollments"><Button variant="ghost" size="sm">View All <ArrowRight className="h-4 w-4 ml-1" /></Button></Link>
            </CardHeader>
            <CardContent>
              {activeEnrollments.length === 0 && waitlistedEnrollments.length === 0 ? (
                <div className="text-center py-6">
                  <Calendar className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground mb-3">No upcoming sessions</p>
                  <div className="flex gap-2 justify-center">
                    <Link to="/catalog"><Button size="sm" variant="outline">Browse Courses</Button></Link>
                    <Link to="/summer-camps"><Button size="sm" className="bg-rsc-yellow text-rsc-navy"><Sun className="h-4 w-4 mr-1" /> Summer Camps</Button></Link>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {activeEnrollments.slice(0, 5).map((enrollment, i) => {
                    const cat = enrollment.course_category || enrollment.section_category || '';
                    const CatIcon = CATEGORY_ICONS[cat] || Calendar;
                    const color = CATEGORY_COLORS[cat] || 'bg-gray-400';
                    
                    // Format date range
                    const formatDateShort = (dateStr) => {
                      if (!dateStr) return '';
                      try {
                        // Parse YYYY-MM-DD as a local date to avoid the UTC
                        // → previous-day drift west of UTC.
                        const ymd = typeof dateStr === 'string' ? dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/) : null;
                        const d = ymd
                          ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
                          : new Date(dateStr);
                        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      } catch {
                        return dateStr;
                      }
                    };
                    
                    // Format time
                    const formatTimeShort = (timeStr) => {
                      if (!timeStr) return '';
                      try {
                        const parts = timeStr.split(':');
                        let hours = parseInt(parts[0], 10);
                        const minutes = parts[1] || '00';
                        const ampm = hours >= 12 ? 'PM' : 'AM';
                        hours = hours % 12 || 12;
                        return `${hours}:${minutes} ${ampm}`;
                      } catch {
                        return timeStr;
                      }
                    };
                    
                    const startDate = enrollment.section_start_date;
                    const endDate = enrollment.section_end_date;
                    const startTime = enrollment.section_start_time;
                    const endTime = enrollment.section_end_time;
                    const meetingDays = enrollment.section_meeting_days;
                    
                    // Build schedule info line
                    let scheduleInfo = '';
                    if (startDate && endDate) {
                      if (String(startDate) === String(endDate)) {
                        scheduleInfo = formatDateShort(startDate);
                      } else {
                        scheduleInfo = `${formatDateShort(startDate)} – ${formatDateShort(endDate)}`;
                      }
                    }
                    if (meetingDays) {
                      const daysStr = Array.isArray(meetingDays) ? meetingDays.join('/') : meetingDays;
                      scheduleInfo += scheduleInfo ? ` · ${daysStr}` : daysStr;
                    }
                    if (startTime && endTime) {
                      scheduleInfo += scheduleInfo ? ` · ${formatTimeShort(startTime)}–${formatTimeShort(endTime)}` : `${formatTimeShort(startTime)}–${formatTimeShort(endTime)}`;
                    }
                    
                    return (
                      <div key={enrollment.id} className="p-3 rounded-lg border flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg ${color} flex items-center justify-center flex-shrink-0`}>
                          <CatIcon className="h-4 w-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{enrollment.section_title || 'Course'}</p>
                          <div className="flex flex-col gap-0.5 text-xs text-muted-foreground">
                            {enrollment.learner_name && <span>{enrollment.learner_name}</span>}
                            {scheduleInfo && <span>{scheduleInfo}</span>}
                          </div>
                        </div>
                        <Badge variant="secondary" className="text-[10px] flex-shrink-0">Confirmed</Badge>
                      </div>
                    );
                  })}
                  {waitlistedEnrollments.length > 0 && (
                    <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                      <p className="text-xs font-semibold text-amber-700 flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" /> {waitlistedEnrollments.length} waitlisted enrollment{waitlistedEnrollments.length > 1 ? 's' : ''}
                      </p>
                      <p className="text-[10px] text-amber-600 mt-0.5">You'll be notified when a spot opens up.</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right column: Referral + Review prompt */}
        <div className="space-y-6">
          {/* Referral Widget */}
          <Card className="card-dashboard border-blue-200 bg-blue-50/30">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base"><Share2 className="h-5 w-5 text-blue-500" /> Refer a Friend</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground mb-3">Share your code — both of you get $50 off!</p>
              {referralCode ? (
                <div className="p-3 rounded-lg bg-white border text-center mb-3">
                  <p className="text-xs text-muted-foreground">Your referral code</p>
                  <p className="text-xl font-mono font-bold tracking-wider text-blue-600">{referralCode}</p>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground mb-3">Loading your referral code...</p>
              )}
              {referralStats && (
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="p-2 rounded bg-white border text-center">
                    <p className="text-lg font-bold text-blue-600">{referralStats.total_referrals || 0}</p>
                    <p className="text-[10px] text-muted-foreground">Referrals</p>
                  </div>
                  <div className="p-2 rounded bg-white border text-center">
                    <p className="text-lg font-bold text-green-600">${referralStats.total_earnings || 0}</p>
                    <p className="text-[10px] text-muted-foreground">Earned</p>
                  </div>
                </div>
              )}
              <Link to="/parent/referral"><Button size="sm" className="w-full" variant="outline">Share & Earn More →</Button></Link>
            </CardContent>
          </Card>

          {/* Review Prompt */}
          <Card className="card-dashboard border-amber-200 bg-amber-50/30">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base"><Star className="h-5 w-5 text-amber-500" /> Leave a Review</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground mb-3">Your feedback helps other parents discover quality STEM education!</p>
              <a href="https://g.page/r/readysetstem/review" target="_blank" rel="noopener noreferrer">
                <Button size="sm" className="w-full bg-amber-500 hover:bg-amber-600 text-white">
                  <Star className="h-4 w-4 mr-1" /> Review on Google
                </Button>
              </a>
              <div className="flex items-center justify-center gap-1 mt-2">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-3 w-3 fill-amber-400 text-amber-400" />)}
                <span className="text-xs text-muted-foreground ml-1">4.9/5 avg</span>
              </div>
            </CardContent>
          </Card>

          {/* Community Impact */}
          <Card className="card-dashboard">
            <CardContent className="p-4 text-center">
              <Heart className="h-5 w-5 text-rose-500 mx-auto mb-2" />
              <p className="text-sm font-semibold mb-1">Powered by APS 501(c)(3)</p>
              <p className="text-[10px] text-muted-foreground">93% of learners attend free. Your enrollment supports the mission.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
